import type {
  Notification,
  NotificationType,
} from '@travel-planner/shared/schemas';

// Presentation copy lives on the CLIENT, not in the stored row.
//
// The server writes one self-contained sentence per notification because a task
// description has to stand alone. The bell does not: it has a headline slot, a
// subject slot, a timestamp slot and an expanded body, so repeating "Traveller
// Preview opened: <trip> — opened for the first time on <date>" in a list row
// wastes all of them on one string. Deriving the copy from `type` here keeps
// rows scannable and leaves the stored text untouched for the task channel.

type EventCopy = {
  /** Short event label — the scannable part of a row. */
  headline: string;
  /** One line of plain language, shown in the toast. */
  description: string;
};

const COPY_BY_TYPE: Record<NotificationType, EventCopy> = {
  traveller_preview_opened: {
    headline: 'Preview opened',
    description: 'A traveller opened the itinerary preview.',
  },
  customer_needs_review: {
    headline: 'Needs consultant review',
    description: 'The customer asked for consultant review on this trip.',
  },
  customer_ready_for_client: {
    headline: 'Ready for client',
    description:
      'The customer marked this trip ready to send to the end client.',
  },
  operational_itinerary_cancelled: {
    headline: 'Itinerary cancelled',
    description: 'An itinerary in operation was cancelled.',
  },
  support_ticket_commented: {
    headline: 'New ticket comment',
    // Only a fallback: the stored body names WHO commented, and the expanded
    // row prefers it (see `detail` in NotificationPresentation).
    description: 'Someone commented on one of your support tickets.',
  },
};

const FALLBACK: EventCopy = {
  headline: 'Notification',
  description: 'Something happened on one of your trips.',
};

/**
 * The stored title is `"<event copy>: <subject>"`. Recover just the subject so
 * the expanded row can show the trip on its own line. Falls back to the whole
 * title when the shape is unrecognised, so an older or future row still renders
 * something meaningful rather than an empty slot.
 */
const subjectOf = (title: string): string | null => {
  const separator = title.indexOf(': ');
  const subject =
    separator === -1 ? title.trim() : title.slice(separator + 2).trim();
  return subject.length > 0 ? subject : null;
};

/** Where "View details" opens an itinerary: its trip page, or Operations. */
export type NotificationItineraryView = 'planner' | 'operations';

const ITINERARY_VIEW_BY_TYPE: Partial<
  Record<NotificationType, NotificationItineraryView>
> = {
  operational_itinerary_cancelled: 'operations',
};

export type NotificationPresentation = EventCopy & {
  /**
   * The event's OWN sentence, for the expanded row. This is the stored body —
   * it carries the detail the type alone cannot: WHO commented on a ticket,
   * that a preview open was the first one. Falls back to the static per-type
   * line for rows written without one.
   *
   * Separate from `description` on purpose: the toast deliberately says only
   * "something happened" and leaves the detail to the drawer.
   */
  detail: string;
  /** The trip or ticket this is about, for the expanded section. */
  subject: string | null;
  /** Itinerary id when the notification points at one. */
  itineraryId: string | null;
  itineraryView: NotificationItineraryView;
  /** Support-ticket id when the notification points at one. */
  supportTicketId: string | null;
};

export const presentNotification = (
  // `body` is optional: an event kind that stores no sentence (the customer
  // Finish-review CTAs) falls back to the static per-type copy.
  notification: Pick<
    Notification,
    'type' | 'title' | 'entityType' | 'entityId'
  > & {
    body?: string | null;
  }
): NotificationPresentation => {
  const copy = COPY_BY_TYPE[notification.type] ?? FALLBACK;
  return {
    ...copy,
    detail: notification.body?.trim() || copy.description,
    subject: subjectOf(notification.title),
    // Each entity type resolves to at most ONE id, and the row derives its
    // "View details" target from whichever is set. An unknown entity type
    // yields neither, and the row simply renders no action.
    itineraryId:
      notification.entityType === 'itinerary' ? notification.entityId : null,
    itineraryView: ITINERARY_VIEW_BY_TYPE[notification.type] ?? 'planner',
    supportTicketId:
      notification.entityType === 'support_ticket'
        ? notification.entityId
        : null,
  };
};
